import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Sun, Moon, ArrowRight } from 'lucide-react';
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { useTheme } from '../context/ThemeContext';
import { useConsultationModal } from '../context/ConsultationModalContext';

const Navbar = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { openModal } = useConsultationModal();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Platform', path: '/platform' },
    { name: 'Products', path: '/products' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Security', path: '/security' },
    { name: 'Compliance', path: '/compliance' },
    { name: 'Portals', path: '/portals' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b ${
        scrolled 
          ? "bg-background/80 backdrop-blur-xl border-border/40 py-3" 
          : "bg-transparent border-transparent py-6"
      }`}
    >
      <div className="container mx-auto px-6 flex items-center justify-between">
        
        {/* LOGO - Left aligned, flex-shrink-0 to prevent crushing */}
        <Link to="/" className="flex items-center gap-3 group relative z-20 flex-shrink-0 mr-8">
          <div className="relative">
             <div className="absolute inset-0 bg-accent/30 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
             <img 
                src="https://customer-assets.emergentagent.com/job_everestcloud/artifacts/iuie7tiv_dns-logo-symbol.png" 
                alt="EverestCloud" 
                className="h-12 w-12 object-contain relative z-10 drop-shadow-xl dark:brightness-100 dark:invert-0 brightness-0 invert-0 transition-all" 
              />
          </div>
          <span className="font-bold text-2xl tracking-tight text-foreground group-hover:text-accent transition-colors hidden sm:inline-block">
            Everest<span className="text-accent">Cloud</span>
          </span>
        </Link>

        {/* DESKTOP NAV - Removed absolute positioning to prevent overlap */}
        <div className="hidden xl:flex items-center gap-1 bg-background/60 backdrop-blur-md px-2 py-1.5 rounded-full border border-white/10 shadow-lg dark:shadow-black/20 mx-auto">
          {navItems.map((item) => (
            <Link
                key={item.name}
                to={item.path}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 relative whitespace-nowrap ${
                  isActive(item.path) 
                  ? "text-background bg-foreground shadow-md font-bold" 
                  : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                }`}
            >
                {item.name}
            </Link>
          ))}
        </div>

        {/* RIGHT ACTIONS - Flex container */}
        <div className="hidden lg:flex items-center gap-4 relative z-20 flex-shrink-0 ml-auto">
          {/* Show reduced nav on Large screens (lg) but hide on XL where full pill is visible */}
          <div className="hidden lg:flex xl:hidden gap-4 mr-4">
             <Link to="/products" className="text-sm font-medium text-muted-foreground hover:text-foreground">Products</Link>
             <Link to="/solutions" className="text-sm font-medium text-muted-foreground hover:text-foreground">Solutions</Link>
          </div>

          <Button variant="ghost" className="text-muted-foreground hover:text-foreground hover:bg-transparent font-medium" asChild>
             <Link to="/contact">Contact</Link>
          </Button>
          
          <Button 
            size="sm" 
            className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-[0_0_20px_-5px_rgba(234,179,8,0.5)] font-bold px-6 rounded-full h-10 transition-all hover:scale-105"
            onClick={() => openModal('demo')}
          >
            Request Demo <ArrowRight className="ml-2 h-4 w-4" />
          </Button>

          <div className="h-6 w-px bg-border/50 mx-1"></div>

          {/* Theme Toggle */}
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={toggleTheme}
            className="text-muted-foreground hover:text-foreground rounded-full hover:bg-white/5 transition-colors border border-transparent hover:border-border"
          >
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>

        {/* MOBILE MENU */}
        <div className="lg:hidden flex items-center gap-4 ml-auto">
            <Button 
                variant="ghost" 
                size="icon" 
                onClick={toggleTheme}
                className="text-muted-foreground"
            >
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-foreground">
                        <Menu className="h-6 w-6" />
                    </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] border-l-border/50 bg-background/95 backdrop-blur-2xl">
                    <div className="flex flex-col space-y-2 mt-8">
                        {navItems.map((item) => (
                            <Link 
                                key={item.name} 
                                to={item.path} 
                                onClick={() => setIsOpen(false)} 
                                className={`text-lg font-medium p-4 rounded-xl transition-all ${
                                    isActive(item.path) 
                                    ? "bg-accent/10 text-accent border border-accent/20" 
                                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                                }`}
                            >
                                {item.name}
                            </Link>
                        ))}
                        <div className="h-px bg-border my-4"></div>
                        <Button 
                            className="w-full bg-accent text-accent-foreground font-bold h-12 text-base"
                            onClick={() => { setIsOpen(false); openModal('demo'); }}
                        >
                            Request Demo
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
